 

import { useState, useEffect, useRef } from "react"
import { useSpring, animated } from "react-spring"
import * as d3 from "d3"
import AlgorithmControls from "./algorithm-controls.js"
import InfoPanel from "./info-panel.js"
import StatsPanel from "./stats-panel.js"
import ComparisonLogs from "./comaprison-logs.js"
import StepControls from "./step-controls.js"
import "../styles/tsp-visualizer.css"

// TSP algorithms
const algorithms = {
  nearestNeighbor: "Nearest Neighbor",
  twoOpt: "2-Opt",
  bruteForce: "Brute Force (exact)",
  genetic: "Genetic Algorithm",
}

export default function TspVisualizer() {
  const [cities, setCities] = useState([])
  const [links, setLinks] = useState([])
  const [currentPath, setCurrentPath] = useState([])
  const [algorithm, setAlgorithm] = useState("nearestNeighbor")
  const [isRunning, setIsRunning] = useState(false)
  const [speed, setSpeed] = useState(50)
  const [totalDistance, setTotalDistance] = useState(0)
  const [step, setStep] = useState(0)
  const [maxSteps, setMaxSteps] = useState(0)
  const [showExplanation, setShowExplanation] = useState(true)
  const [allSteps, setAllSteps] = useState([])
  const [stepLogs, setStepLogs] = useState([])
  const [autoPlay, setAutoPlay] = useState(false)

  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const graphContainerRef = useRef(null)

  // Get dynamic width and height based on container size
  const getGraphDimensions = () => {
    if (!graphContainerRef.current) return { width: 800, height: 600 }
    const rect = graphContainerRef.current.getBoundingClientRect()
    return { width: rect.width, height: rect.height }
  }

  // Animation properties
  const fadeIn = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: { duration: 1000 },
  })

  // Generate random cities
  const generateRandomCities = (count) => {
    const { width, height } = getGraphDimensions()
    const padding = 50 // Padding from edges

    const newCities = []
    for (let i = 0; i < count; i++) {
      newCities.push({
        id: `city-${i}`,
        name: `City ${i + 1}`,
        x: Math.random() * (width - padding * 2) + padding,
        y: Math.random() * (height - padding * 2) + padding,
      })
    }
    setCities(newCities)
    generateLinks(newCities)
  }

  // Generate all possible links between cities
  const generateLinks = (cityList) => {
    const newLinks = []
    for (let i = 0; i < cityList.length; i++) {
      for (let j = i + 1; j < cityList.length; j++) {
        const cityA = cityList[i]
        const cityB = cityList[j]
        const dx = cityA.x - cityB.x
        const dy = cityA.y - cityB.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        newLinks.push({
          source: cityA.id,
          target: cityB.id,
          sourceX: cityA.x,
          sourceY: cityA.y,
          targetX: cityB.x,
          targetY: cityB.y,
          distance,
          inPath: false,
        })
      }
    }
    setLinks(newLinks)
  }

  // Add a city on click
  const handleAddCity = (event) => {
    if (isRunning) return

    const rect = graphContainerRef.current.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    // Ensure the city is within the bounds of the graph
    const { width, height } = getGraphDimensions()
    const padding = 20

    const boundedX = Math.min(Math.max(x, padding), width - padding)
    const boundedY = Math.min(Math.max(y, padding), height - padding)

    const newCity = {
      id: `city-${cities.length}`,
      name: `City ${cities.length + 1}`,
      x: boundedX,
      y: boundedY,
    }

    const newCities = [...cities, newCity]
    setCities(newCities)
    generateLinks(newCities)
  }

  // Clear all cities
  const handleClear = () => {
    setCities([])
    setLinks([])
    setCurrentPath([])
    setTotalDistance(0)
    setStep(0)
    setMaxSteps(0)
    setIsRunning(false)
    setAllSteps([])
    setStepLogs([])
    setAutoPlay(false)
  }

  // Run the selected algorithm
  const runAlgorithm = () => {
    if (cities.length < 3) {
      alert("Please add at least 3 cities")
      return
    }

    setIsRunning(true)
    setStep(0)
    setAutoPlay(false)

    // Reset path
    setLinks(links.map((link) => ({ ...link, inPath: false })))

    let path = []
    let steps = []
    let logs = []

    switch (algorithm) {
      case "nearestNeighbor":
        const result = nearestNeighborAlgorithm(cities, links)
        path = result.path
        steps = result.steps
        logs = result.logs
        break
      case "twoOpt":
        const twoOptResult = twoOptAlgorithm(cities, links)
        path = twoOptResult.path
        steps = twoOptResult.steps
        logs = twoOptResult.logs
        break
      case "bruteForce":
        if (cities.length > 10) {
          alert("Brute force is too slow for more than 10 cities")
          setIsRunning(false)
          return
        }
        const bruteForceResult = bruteForceAlgorithm(cities, links)
        path = bruteForceResult.path
        steps = bruteForceResult.steps
        logs = bruteForceResult.logs
        break
      case "genetic":
        const geneticResult = geneticAlgorithm(cities, links)
        path = geneticResult.path
        steps = geneticResult.steps
        logs = geneticResult.logs
        break
    }

    setMaxSteps(steps.length)
    setAllSteps(steps)
    setStepLogs(logs)

    // Show the first step
    if (steps.length > 0) {
      updatePath(steps[0])
      setStep(1)
    }
  }

  // Handle step forward
  const handleStepForward = () => {
    if (step < maxSteps) {
      updatePath(allSteps[step])
      setStep(step + 1)
    }
  }

  // Handle step backward
  const handleStepBackward = () => {
    if (step > 1) {
      updatePath(allSteps[step - 2])
      setStep(step - 1)
    }
  }

  // Skip to first step
  const handleSkipToStart = () => {
    if (allSteps.length > 0) {
      updatePath(allSteps[0])
      setStep(1)
    }
  }

  // Skip to last step
  const handleSkipToEnd = () => {
    if (allSteps.length > 0) {
      updatePath(allSteps[allSteps.length - 1])
      setStep(maxSteps)
    }
  }

  // Toggle autoplay
  const toggleAutoPlay = () => {
    setAutoPlay(!autoPlay)
  }

  // Autoplay effect
  useEffect(() => {
    let interval

    if (autoPlay && step < maxSteps) {
      interval = setInterval(
        () => {
          if (step < maxSteps) {
            updatePath(allSteps[step])
            setStep(step + 1)
          } else {
            setAutoPlay(false)
          }
        },
        1000 - speed * 9,
      )
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [autoPlay, step, maxSteps, allSteps, speed])

  // Update the current path visualization
  const updatePath = (path) => {
    setCurrentPath(path)

    // Update links to show which are in the path
    const newLinks = links.map((link) => {
      const isInPath = path.some((cityId, index) => {
        const nextIndex = (index + 1) % path.length
        return (
          (link.source === path[index] && link.target === path[nextIndex]) ||
          (link.source === path[nextIndex] && link.target === path[index])
        )
      })

      return { ...link, inPath: isInPath }
    })

    setLinks(newLinks)

    // Calculate total distance
    let distance = 0
    for (let i = 0; i < path.length; i++) {
      const cityA = path[i]
      const cityB = path[(i + 1) % path.length]

      const link = links.find(
        (l) => (l.source === cityA && l.target === cityB) || (l.source === cityB && l.target === cityA),
      )

      if (link) {
        distance += link.distance
      }
    }

    setTotalDistance(Math.round(distance))
  }

  // Nearest Neighbor Algorithm
  const nearestNeighborAlgorithm = (cities, links) => {
    const path = []
    const steps = []
    const logs = []

    if (cities.length === 0) return { path, steps, logs }

    // Start with the first city
    const startCity = cities[0]
    path.push(startCity.id)
    steps.push([...path])
    logs.push({
      title: "Initialization",
      description: `Starting at ${startCity.name}`,
      details: "The algorithm begins by selecting a starting city.",
    })

    const unvisited = cities.slice(1).map((city) => city.id)

    while (unvisited.length > 0) {
      const currentCity = path[path.length - 1]
      const currentCityObj = cities.find((c) => c.id === currentCity)
      let nearestCity = ""
      let nearestCityObj = null
      let minDistance = Number.POSITIVE_INFINITY

      // Find the nearest unvisited city
      for (const cityId of unvisited) {
        const cityObj = cities.find((c) => c.id === cityId)
        const link = links.find(
          (l) => (l.source === currentCity && l.target === cityId) || (l.source === cityId && l.target === currentCity),
        )

        if (link && link.distance < minDistance) {
          minDistance = link.distance
          nearestCity = cityId
          nearestCityObj = cityObj
        }
      }

      // Add the nearest city to the path
      path.push(nearestCity)
      steps.push([...path])

      logs.push({
        title: "Finding Nearest Neighbor",
        description: `From ${currentCityObj.name}, the nearest city is ${nearestCityObj.name} (distance: ${minDistance.toFixed(2)})`,
        details: "The algorithm selects the unvisited city with the shortest distance from the current city.",
      })

      // Remove the city from unvisited
      const index = unvisited.indexOf(nearestCity)
      unvisited.splice(index, 1)
    }

    // Add the final step (complete tour)
    const completePath = [...path, path[0]]
    steps.push(completePath)

    logs.push({
      title: "Completing the Tour",
      description: "Returning to the starting city to complete the tour",
      details: "The algorithm completes the tour by returning to the starting city.",
    })

    return { path: completePath, steps, logs }
  }

  // 2-Opt Algorithm
  const twoOptAlgorithm = (cities, links) => {
    // Start with nearest neighbor solution
    const nnResult = nearestNeighborAlgorithm(cities, links)
    let bestPath = nnResult.path
    const steps = [bestPath]
    const logs = [
      {
        title: "Initialization",
        description: "Starting with the Nearest Neighbor solution",
        details:
          "The 2-Opt algorithm begins with an initial tour, typically generated by the Nearest Neighbor algorithm.",
      },
    ]

    let improved = true
    let iterations = 0
    const maxIterations = 100 // Limit iterations to prevent infinite loops

    while (improved && iterations < maxIterations) {
      improved = false
      iterations++

      for (let i = 1; i < bestPath.length - 2; i++) {
        for (let j = i + 1; j < bestPath.length - 1; j++) {
          // Skip adjacent edges
          if (j - i === 1) continue

          // Calculate current distance
          const a = bestPath[i - 1]
          const b = bestPath[i]
          const c = bestPath[j]
          const d = bestPath[j + 1]

          const cityA = cities.find((city) => city.id === a)
          const cityB = cities.find((city) => city.id === b)
          const cityC = cities.find((city) => city.id === c)
          const cityD = cities.find((city) => city.id === d)

          const ab = links.find((l) => (l.source === a && l.target === b) || (l.source === b && l.target === a))

          const cd = links.find((l) => (l.source === c && l.target === d) || (l.source === d && l.target === c))

          const ac = links.find((l) => (l.source === a && l.target === c) || (l.source === c && l.target === a))

          const bd = links.find((l) => (l.source === b && l.target === d) || (l.source === d && l.target === b))

          if (!ab || !cd || !ac || !bd) continue

          const currentDistance = ab.distance + cd.distance
          const newDistance = ac.distance + bd.distance

          // If the new path is shorter, swap the edges
          if (newDistance < currentDistance) {
            const newPath = [...bestPath]
            // Reverse the segment between i and j
            const segment = newPath.slice(i, j + 1).reverse()
            newPath.splice(i, j - i + 1, ...segment)

            bestPath = newPath
            steps.push([...bestPath])

            logs.push({
              title: `Iteration ${iterations}: Improvement Found`,
              description: `Swapping edges (${cityA.name}-${cityB.name} and ${cityC.name}-${cityD.name}) with (${cityA.name}-${cityC.name} and ${cityB.name}-${cityD.name})`,
              details: `The algorithm found a better tour by replacing two edges. This reduced the total distance by ${(currentDistance - newDistance).toFixed(2)}.`,
            })

            improved = true
            break
          }
        }
        if (improved) break
      }

      if (!improved && iterations < maxIterations) {
        logs.push({
          title: `Iteration ${iterations}: No Improvement`,
          description: "No further improvements found",
          details:
            "The algorithm has reached a local optimum and cannot find any more edge swaps that would reduce the total distance.",
        })
      }
    }

    return { path: bestPath, steps, logs }
  }

  // Brute Force Algorithm (exact solution)
  const bruteForceAlgorithm = (cities, links) => {
    const path = []
    const steps = []
    const logs = []

    if (cities.length === 0) return { path, steps, logs }

    // Get all city IDs except the first one (which will be our starting point)
    const startCity = cities[0].id
    const startCityObj = cities.find((city) => city.id === startCity)
    const otherCities = cities.slice(1).map((city) => city.id)

    logs.push({
      title: "Initialization",
      description: `Starting at ${startCityObj.name} and generating all possible permutations`,
      details: `The Brute Force algorithm will examine all (n-1)! possible routes, where n is the number of cities. For ${cities.length} cities, this means ${factorial(cities.length - 1)} permutations.`,
    })

    // Generate all permutations of the other cities
    const permutations = getPermutations(otherCities)

    let bestPath = []
    let minDistance = Number.POSITIVE_INFINITY
    let permCount = 0

    // Evaluate each permutation
    for (const perm of permutations) {
      permCount++
      const currentPath = [startCity, ...perm, startCity]
      steps.push([...currentPath])

      let distance = 0
      for (let i = 0; i < currentPath.length - 1; i++) {
        const cityA = currentPath[i]
        const cityB = currentPath[i + 1]

        const link = links.find(
          (l) => (l.source === cityA && l.target === cityB) || (l.source === cityB && l.target === cityA),
        )

        if (link) {
          distance += link.distance
        }
      }

      const permCities = currentPath
        .map((id) => {
          const city = cities.find((c) => c.id === id)
          return city ? city.name : id
        })
        .join(" → ")

      if (distance < minDistance) {
        minDistance = distance
        bestPath = currentPath

        logs.push({
          title: `Permutation ${permCount}: New Best Path`,
          description: `Path: ${permCities}`,
          details: `Distance: ${distance.toFixed(2)} (new best)`,
        })
      } else {
        logs.push({
          title: `Permutation ${permCount}: Suboptimal Path`,
          description: `Path: ${permCities}`,
          details: `Distance: ${distance.toFixed(2)} (current best: ${minDistance.toFixed(2)})`,
        })
      }
    }

    // Add the best path as the final step
    steps.push([...bestPath])

    logs.push({
      title: "Final Result",
      description: "Found the optimal solution",
      details: `After examining all ${permutations.length} permutations, the optimal path has a distance of ${minDistance.toFixed(2)}.`,
    })

    return { path: bestPath, steps, logs }
  }

  // Helper function to calculate factorial
  const factorial = (n) => {
    if (n <= 1) return 1
    return n * factorial(n - 1)
  }

  // Helper function to generate all permutations
  const getPermutations = (arr) => {
    if (arr.length <= 1) return [arr]

    const result = []

    for (let i = 0; i < arr.length; i++) {
      const current = arr[i]
      const remaining = [...arr.slice(0, i), ...arr.slice(i + 1)]
      const permutations = getPermutations(remaining)

      for (const perm of permutations) {
        result.push([current, ...perm])
      }
    }

    return result
  }

  // Genetic Algorithm
  const geneticAlgorithm = (cities, links) => {
    const populationSize = 50
    const generations = 100
    const mutationRate = 0.01
    const steps = []
    const logs = []

    // Start with the first city
    const startCity = cities[0].id
    const startCityObj = cities.find((city) => city.id === startCity)

    logs.push({
      title: "Initialization",
      description: `Creating initial population of ${populationSize} random tours`,
      details: "The genetic algorithm begins by creating a diverse population of potential solutions (tours).",
    })

    // Generate initial population
    let population = []
    for (let i = 0; i < populationSize; i++) {
      const otherCities = cities.slice(1).map((city) => city.id)
      shuffleArray(otherCities)
      population.push([startCity, ...otherCities, startCity])
    }

    // Evaluate fitness (1/distance)
    const evaluateFitness = (path) => {
      let distance = 0
      for (let i = 0; i < path.length - 1; i++) {
        const cityA = path[i]
        const cityB = path[i + 1]

        const link = links.find(
          (l) => (l.source === cityA && l.target === cityB) || (l.source === cityB && l.target === cityA),
        )

        if (link) {
          distance += link.distance
        }
      }

      return { fitness: 1 / distance, distance }
    }

    let bestPath = population[0]
    const bestFitnessResult = evaluateFitness(bestPath)
    let bestFitness = bestFitnessResult.fitness
    let bestDistance = bestFitnessResult.distance

    // Record initial best path
    steps.push([...bestPath])

    logs.push({
      title: "Initial Best Tour",
      description: `Best tour in initial population has distance: ${bestDistance.toFixed(2)}`,
      details: "The algorithm identifies the best tour in the randomly generated initial population.",
    })

    // Run for a fixed number of generations
    for (let gen = 0; gen < generations; gen++) {
      // Select parents using tournament selection
      const tournamentSize = 5
      const newPopulation = []

      for (let i = 0; i < populationSize; i++) {
        // Tournament selection
        const tournament = []
        for (let j = 0; j < tournamentSize; j++) {
          const randomIndex = Math.floor(Math.random() * population.length)
          tournament.push(population[randomIndex])
        }

        // Find the best individual in the tournament
        let bestIndividual = tournament[0]
        let bestIndividualFitness = evaluateFitness(bestIndividual).fitness

        for (let j = 1; j < tournament.length; j++) {
          const fitness = evaluateFitness(tournament[j]).fitness
          if (fitness > bestIndividualFitness) {
            bestIndividual = tournament[j]
            bestIndividualFitness = fitness
          }
        }

        newPopulation.push(bestIndividual)
      }

      if (gen % 10 === 0 || gen === generations - 1) {
        logs.push({
          title: `Generation ${gen + 1}`,
          description: `Selection: Tournament selection with size ${tournamentSize}`,
          details:
            "Tournament selection randomly picks a small group of individuals and selects the best one for reproduction.",
        })
      }

      // Crossover and mutation
      for (let i = 0; i < newPopulation.length; i += 2) {
        if (i + 1 < newPopulation.length) {
          // Ordered crossover
          const parent1 = newPopulation[i]
          const parent2 = newPopulation[i + 1]

          // Remove start/end city for crossover
          const p1 = parent1.slice(1, parent1.length - 1)
          const p2 = parent2.slice(1, parent2.length - 1)

          // Select crossover points
          const point1 = Math.floor(Math.random() * p1.length)
          const point2 = Math.floor(Math.random() * (p1.length - point1)) + point1

          // Create children
          const child1 = orderedCrossover(p1, p2, point1, point2)
          const child2 = orderedCrossover(p2, p1, point1, point2)

          // Add start/end city back
          child1.unshift(startCity)
          child1.push(startCity)
          child2.unshift(startCity)
          child2.push(startCity)

          // Mutation
          if (Math.random() < mutationRate) {
            mutate(child1)
          }
          if (Math.random() < mutationRate) {
            mutate(child2)
          }

          newPopulation[i] = child1
          newPopulation[i + 1] = child2
        }
      }

      if (gen % 10 === 0 || gen === generations - 1) {
        logs.push({
          title: `Generation ${gen + 1}`,
          description: "Crossover: Ordered Crossover (OX)",
          details:
            "Ordered Crossover preserves the relative order of cities from both parents while creating valid tours.",
        })

        logs.push({
          title: `Generation ${gen + 1}`,
          description: `Mutation: Swap Mutation (rate: ${mutationRate})`,
          details:
            "Swap mutation randomly exchanges the position of two cities to introduce diversity in the population.",
        })
      }

      population = newPopulation

      // Find the best individual
      let generationBestPath = null
      let generationBestDistance = Number.POSITIVE_INFINITY

      for (const individual of population) {
        const fitnessResult = evaluateFitness(individual)
        const fitness = fitnessResult.fitness
        const distance = fitnessResult.distance

        if (fitness > bestFitness) {
          bestFitness = fitness
          bestDistance = distance
          bestPath = individual
          steps.push([...bestPath])

          generationBestPath = individual
          generationBestDistance = distance
        }

        if (distance < generationBestDistance) {
          generationBestPath = individual
          generationBestDistance = distance
        }
      }

      if (gen % 10 === 0 || gen === generations - 1) {
        if (generationBestPath) {
          const pathCities = generationBestPath
            .map((id) => {
              const city = cities.find((c) => c.id === id)
              return city ? city.name : id
            })
            .join(" → ")

          logs.push({
            title: `Generation ${gen + 1}`,
            description: `Best tour in generation: distance ${generationBestDistance.toFixed(2)}`,
            details: `Overall best tour so far: distance ${bestDistance.toFixed(2)}`,
          })
        }
      }
    }

    // Ensure we have the final best path
    if (steps[steps.length - 1] !== bestPath) {
      steps.push([...bestPath])
    }

    logs.push({
      title: "Final Result",
      description: `Best tour found has distance: ${bestDistance.toFixed(2)}`,
      details:
        "The genetic algorithm has completed all generations and found the best solution in the final population.",
    })

    return { path: bestPath, steps, logs }
  }

  // Helper function for ordered crossover
  const orderedCrossover = (parent1, parent2, point1, point2) => {
    const child = Array(parent1.length).fill("")

    // Copy segment from parent1
    for (let i = point1; i <= point2; i++) {
      child[i] = parent1[i]
    }

    // Fill remaining positions with cities from parent2 in order
    let j = 0
    for (let i = 0; i < parent2.length; i++) {
      if (!child.includes(parent2[i])) {
        // Find next empty position
        while (j < child.length && child[j] !== "") {
          j++
        }

        if (j < child.length) {
          child[j] = parent2[i]
        }
      }
    }

    return child
  }

  // Helper function for mutation (swap mutation)
  const mutate = (path) => {
    // Don't mutate the start/end city
    const idx1 = Math.floor(Math.random() * (path.length - 2)) + 1
    let idx2 = Math.floor(Math.random() * (path.length - 2)) + 1

    // Ensure different indices
    while (idx1 === idx2) {
      idx2 = Math.floor(Math.random() * (path.length - 2)) + 1
    }

    // Swap cities
    const temp = path[idx1]
    path[idx1] = path[idx2]
    path[idx2] = temp
  }

  // Helper function to shuffle an array
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
  }

  // D3.js visualization
  useEffect(() => {
    if (!svgRef.current || cities.length === 0) return

    const { width, height } = getGraphDimensions()

    const svg = d3.select(svgRef.current)
    svg.selectAll("*").remove()

    // Set SVG dimensions
    svg.attr("width", width).attr("height", height)

    // Draw links
    const linkElements = svg
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("x1", (d) => d.sourceX)
      .attr("y1", (d) => d.sourceY)
      .attr("x2", (d) => d.targetX)
      .attr("y2", (d) => d.targetY)
      .attr("stroke", (d) => (d.inPath ? "#22c55e" : "rgba(255,255,255,0.12)"))
      .attr("stroke-width", (d) => (d.inPath ? 3 : 1))

    // Draw cities
    const cityElements = svg
      .selectAll("g")
      .data(cities)
      .enter()
      .append("g")
      .attr("transform", (d) => `translate(${d.x}, ${d.y})`)
      .call(
        d3
          .drag()
          .on("start", (event, d) => {
            if (isRunning) return
          })
          .on("drag", (event, d) => {
            if (isRunning) return

            d.x = event.x
            d.y = event.y

            // Keep cities within bounds
            const { width, height } = getGraphDimensions()
            const padding = 20
            d.x = Math.min(Math.max(d.x, padding), width - padding)
            d.y = Math.min(Math.max(d.y, padding), height - padding)

            // Update city position
            d3.select(event.sourceEvent.target.parentNode).attr("transform", `translate(${d.x}, ${d.y})`)

            // Update links connected to this city
            links.forEach((link) => {
              if (link.source === d.id) {
                link.sourceX = d.x
                link.sourceY = d.y
              }
              if (link.target === d.id) {
                link.targetX = d.x
                link.targetY = d.y
              }
            })

            // Update link positions
            svg
              .selectAll("line")
              .attr("x1", (l) => l.sourceX)
              .attr("y1", (l) => l.sourceY)
              .attr("x2", (l) => l.targetX)
              .attr("y2", (l) => l.targetY)

            // Recalculate distances
            generateLinks(cities)
          }),
      )

    // Add city circles
    cityElements
      .append("circle")
      .attr("r", 8)
      .attr("fill", "#60a5fa")
      .attr("stroke", "rgba(255,255,255,0.3)")
      .attr("stroke-width", 1.5)

    // Add city labels
    cityElements
      .append("text")
      .text((d) => d.name)
      .attr("text-anchor", "middle")
      .attr("dy", -12)
      .attr("fill", "rgba(255,255,255,0.75)")
      .attr("font-size", "12px")
      .attr("font-weight", "600")
  }, [cities, links, isRunning])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current && cities.length > 0) {
        const { width, height } = getGraphDimensions()
        d3.select(svgRef.current).attr("width", width).attr("height", height)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [cities])

  // Initialize with some random cities
  useEffect(() => {
    generateRandomCities(5)
  }, [])

  return (
    <animated.div style={fadeIn} className="tsp-container" ref={containerRef}>
      <div className="tsp-header">
        <h2>Traveling Salesman Problem</h2>
        <button className="toggle-explanation-btn" onClick={() => setShowExplanation(!showExplanation)}>
          {showExplanation ? "Hide Explanation" : "Show Explanation"}
        </button>
      </div>

      {showExplanation && <InfoPanel />}

      <div className="tsp-content">
        <div className="tspsidebar">
          <AlgorithmControls
            algorithm={algorithm}
            setAlgorithm={setAlgorithm}
            isRunning={isRunning}
            runAlgorithm={runAlgorithm}
            handleClear={handleClear}
            speed={speed}
            setSpeed={setSpeed}
            generateRandomCities={generateRandomCities}
          />

          <StatsPanel
            cities={cities}
            totalDistance={totalDistance}
            step={step}
            maxSteps={maxSteps}
            algorithm={algorithms[algorithm]}
          />

          {isRunning && (
            <StepControls
              step={step}
              maxSteps={maxSteps}
              onStepForward={handleStepForward}
              onStepBackward={handleStepBackward}
              onSkipToStart={handleSkipToStart}
              onSkipToEnd={handleSkipToEnd}
              autoPlay={autoPlay}
              toggleAutoPlay={toggleAutoPlay}
            />
          )}

          {isRunning && stepLogs.length > 0 && <ComparisonLogs logs={stepLogs} currentStep={step - 1} />}
        </div>

        <div className="graph-container" onClick={handleAddCity} ref={graphContainerRef}>
          <svg ref={svgRef} className="graph-svg"></svg>
          <div className="graph-instructions">Click anywhere to add a city</div>
        </div>
      </div>
    </animated.div>
  )
}